/**
 * Status Style Configuration
 *
 * Centralized styling for all status badges and labels throughout the app.
 * This ensures consistent visual representation of statuses.
 */

import type {
  EventStatus,
  AssignmentStatus,
  AvailabilityStatus,
  UserStatus,
  MemberState,
  UserRole,
} from '../types/models';

// Re-export types for backwards compatibility
export type { EventStatus, AssignmentStatus, AvailabilityStatus, UserStatus, MemberState, UserRole };

// =============================================================================
// Types
// =============================================================================

export interface StatusConfig {
  label: string;
  className: string;
}

export interface AvailabilityOptionConfig {
  value: AvailabilityStatus;
  label: string;
  description: string;
  color: string;
}

// =============================================================================
// Event Status
// =============================================================================

export const EVENT_STATUS: Record<EventStatus, StatusConfig> = {
  DRAFT: {
    label: 'Draft',
    className: 'border border-hairline bg-white text-ink-body',
  },
  PUBLISHED: {
    label: 'Published',
    className: 'border border-preview bg-white text-preview-text',
  },
  COMPLETED: {
    label: 'Completed',
    className: 'border border-rack bg-rack text-white',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'border border-program bg-white text-program-dark',
  },
};

// =============================================================================
// Assignment Status
// =============================================================================

export const ASSIGNMENT_STATUS: Record<AssignmentStatus, StatusConfig> = {
  ASSIGNED: {
    label: 'Pending',
    className: 'border border-maybe bg-white text-maybe-dark',
  },
  CONFIRMED: {
    label: 'Confirmed',
    className: 'border border-preview bg-white text-preview-text',
  },
  DECLINED: {
    label: 'Declined',
    className: 'border border-program bg-white text-program-dark',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'border border-hairline bg-paper text-ink-muted',
  },
};

// =============================================================================
// Availability Status
// =============================================================================

export const AVAILABILITY_STATUS: Record<AvailabilityStatus, StatusConfig> = {
  AVAILABLE: {
    label: 'Available',
    className: 'border border-preview bg-white text-preview-text',
  },
  MAYBE: {
    label: 'Maybe',
    className: 'border border-maybe bg-white text-maybe-dark',
  },
  NOT_AVAILABLE: {
    label: 'Not Available',
    className: 'border border-program bg-white text-program-dark',
  },
};

// =============================================================================
// User Status
// =============================================================================

export const USER_STATUS: Record<UserStatus, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    className: 'border border-preview bg-white text-preview-text',
  },
  INACTIVE: {
    label: 'Inactive',
    className: 'border border-hairline bg-paper text-ink-muted',
  },
  PENDING: {
    label: 'Pending',
    className: 'border border-maybe bg-white text-maybe-dark',
  },
};

// =============================================================================
// Member State (standing in the CURRENT org)
// =============================================================================

/**
 * Neither non-active state is an alarm, so neither is red. Archiving is a
 * filing decision — the person is fine, they are just not on this roster —
 * and deactivation is deliberate and reversible. Red is reserved for things
 * that need someone to act.
 */
export const MEMBER_STATE: Record<MemberState, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    className: 'border border-preview bg-white text-preview-text',
  },
  ARCHIVED: {
    label: 'Archived',
    className: 'border border-hairline bg-paper text-ink-secondary',
  },
  DEACTIVATED: {
    label: 'Deactivated',
    className: 'border border-ink-muted bg-white text-ink-muted',
  },
};

// =============================================================================
// User Role
// =============================================================================

export const ROLE_CONFIG: Record<UserRole, StatusConfig> = {
  OWNER: {
    label: 'Owner',
    className: 'border border-rack bg-rack text-white',
  },
  ADMIN: {
    label: 'Admin',
    className: 'border border-rack bg-white text-ink',
  },
  CREW: {
    label: 'Crew',
    className: 'border border-hairline bg-white text-ink-body',
  },
};

// =============================================================================
// Availability Options (for modals with descriptions)
// =============================================================================

export const AVAILABILITY_OPTIONS: AvailabilityOptionConfig[] = [
  {
    value: 'AVAILABLE',
    label: 'Available',
    description: 'I can work this event',
    color: 'border-preview bg-white text-preview-text hover:bg-preview hover:text-white',
  },
  {
    value: 'MAYBE',
    label: 'Maybe',
    description: 'I might be available',
    color: 'border-maybe bg-white text-maybe-dark hover:bg-maybe hover:text-white',
  },
  {
    value: 'NOT_AVAILABLE',
    label: 'Not Available',
    description: 'I cannot work this event',
    color: 'border-program bg-white text-program-dark hover:bg-program hover:text-white',
  },
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get status config with fallback for unknown statuses
 */
export function getStatusConfig<T extends string>(
  statusMap: Record<T, StatusConfig>,
  status: T | string,
  fallback: StatusConfig = { label: 'Unknown', className: 'border border-hairline bg-white text-ink-body' }
): StatusConfig {
  return (statusMap as Record<string, StatusConfig>)[status] || fallback;
}
